import axios from "axios";

/**
 * Resolve the API base URL.
 *
 * Strategy (in order):
 *  1. If REACT_APP_BACKEND_URL is set AND we're not running in a browser,
 *     use it (server-side rendering / tests).
 *  2. In the browser, use window.location.origin — this means the same
 *     bundle runs correctly on preview AND production (and any future
 *     custom domain) without rebuilding. The Emergent ingress routes
 *     /api/* on whatever domain the user lands on to the backend pod.
 *  3. Fall back to REACT_APP_BACKEND_URL if window is somehow unavailable.
 */
function resolveBackendUrl() {
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin;
    }
    return process.env.REACT_APP_BACKEND_URL || "";
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Resolve an asset URL that may be a relative `/api/...` path or already absolute.
 *
 * Strategy:
 *  - If the path is already absolute, return as-is.
 *  - In a browser, anchor `/api/...` to `window.location.origin`. The Emergent
 *    ingress (preview) AND the user's own Cloudflare (production at
 *    myecotour.in) both proxy `/api/*` to the FastAPI pod, so same-origin
 *    is always correct. This avoids the previous bug where production builds
 *    baked the preview hostname into asset URLs, causing prod pages to load
 *    every image cross-origin from the preview pod (which kills Cloudflare
 *    caching, adds a DNS hop, and PSI flags as 3rd-party).
 *  - SSR / build-time fall back to REACT_APP_BACKEND_URL so prerender.js
 *    keeps emitting absolute URLs in static HTML for crawlers that don't
 *    execute JS.
 */
export function assetUrl(path) {
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;
    if (!path.startsWith("/api/")) return path;
    if (typeof window !== "undefined" && window.location?.origin) {
        return `${window.location.origin}${path}`;
    }
    const host = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    return `${host}${path}`;
}

export { BACKEND_URL };

const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
